import { useCallback, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ANALYTICS_TEXT_VERSION,
  getCookieConsent,
  setCookieConsent,
} from "@libertify/ui";
import type { components } from "@libertify/api-types/api";
import instance from "@/services/api";
import {
  getRestorableConsent,
  type ConsentDecision,
  type ConsentMethod,
  type ConsentResponse,
} from "@/services/analyticsConsent";

const CONSENTS_URL = "/api/users/me/consents";

type RecordConsentRequest = components["schemas"]["RecordConsentRequest"];

/**
 * The user's consent records. Restores the analytics answer stored on the server
 * when this browser has none (new browser, cleared storage), so the banner doesn't ask again.
 */
export const useConsentsApi = ({
  userId,
  enabled,
}: {
  userId: number | undefined;
  enabled: boolean;
}) => {
  const query = useQuery({
    queryKey: ["users", "me", "consents", userId],
    queryFn: async () => {
      const response = await instance.get<ConsentResponse[]>(CONSENTS_URL);
      return response.data;
    },
    enabled: enabled && userId !== undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Proof of the choice for the backend log; a failure never blocks the UI
  const { mutate } = useMutation({
    mutationFn: (body: RecordConsentRequest) =>
      instance.post(CONSENTS_URL, body),
    onError: (error) => {
      console.error("[Analytics] Failed to record consent", error);
    },
  });

  const recordAnalyticsConsent = useCallback(
    (decision: ConsentDecision, method: ConsentMethod) =>
      mutate({
        consent_type: "analytics",
        decision,
        text_version: ANALYTICS_TEXT_VERSION,
        method,
      }),
    [mutate]
  );

  useEffect(() => {
    // An answer given in the meantime wins over the server one
    if (!query.data || getCookieConsent() !== null) return;
    const restored = getRestorableConsent(query.data);
    if (restored) setCookieConsent(restored.value, restored.date);
  }, [query.data]);

  // v4: isLoading stays true for a disabled query; a failed request falls back to the banner
  return { isRestoring: query.isInitialLoading, recordAnalyticsConsent };
};
